import React, { useMemo } from 'react';

const STATUS_COR = {
  concluido:    { bg: '#28a745' },
  em_andamento: { bg: '#f97316' },
  atrasado:     { bg: '#dc2626' },
  atrasada:     { bg: '#dc2626' },
  pendente:     { bg: '#9ca3af' },
  cancelado:    { bg: '#d1d5db' },
};

function parseData(str) {
  if (!str) return null;
  const s = String(str).split('T')[0];
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatMes(date) {
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
}

function gerarMarcadores(minDate, maxDate) {
  const marcadores = [];
  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const fim = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 1);
  while (cursor < fim) {
    marcadores.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return marcadores;
}

function pct(date, minDate, totalMs) {
  return Math.max(0, Math.min(100, ((date - minDate) / totalMs) * 100));
}

export default function GanttServicos({ servicos, prazoCombinado }) {
  const servicosComData = useMemo(
    () => (servicos || []).filter(s => s.data_inicio && s.data_fim),
    [servicos]
  );

  const { minDate, maxDate, totalMs, marcadores, prazoDate } = useMemo(() => {
    if (servicosComData.length === 0) return {};

    const datas = servicosComData.flatMap(s => [parseData(s.data_inicio), parseData(s.data_fim)]);
    const prazoDate = prazoCombinado ? parseData(prazoCombinado) : null;
    if (prazoDate) datas.push(prazoDate);

    let minDate = new Date(Math.min(...datas));
    let maxDate = new Date(Math.max(...datas));

    minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 2, 0);

    const totalMs = maxDate - minDate;
    const marcadores = gerarMarcadores(minDate, maxDate);

    return { minDate, maxDate, totalMs, marcadores, prazoDate };
  }, [servicosComData, prazoCombinado]);

  if (servicosComData.length === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
        Nenhum serviço com datas definidas. Adicione datas de início e fim nos serviços para visualizar o cronograma.
      </div>
    );
  }

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 600 }}>

        {/* Legenda */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { bg: '#9ca3af', label: 'Pendente' },
            { bg: '#f97316', label: 'Em andamento' },
            { bg: '#28a745', label: 'Concluído' },
            { bg: '#dc2626', label: 'Atrasado' },
          ].map(v => (
            <div key={v.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: v.bg }} />
              {v.label}
            </div>
          ))}
          {prazoDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: 'transparent', border: '2px dashed #dc2626' }} />
              Prazo da obra
            </div>
          )}
        </div>

        {/* Eixo de tempo */}
        <div style={{ display: 'flex', marginLeft: 160, marginBottom: 4, position: 'relative', height: 20 }}>
          {marcadores.map((m) => (
            <div
              key={m.toISOString()}
              style={{
                position: 'absolute',
                left: `${pct(m, minDate, totalMs)}%`,
                fontSize: 11,
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
                transform: 'translateX(-50%)',
              }}
            >
              {formatMes(m)}
            </div>
          ))}
        </div>

        {/* Linhas dos serviços */}
        <div style={{ position: 'relative' }}>
          {servicosComData.map((s) => {
            const inicio = parseData(s.data_inicio);
            const fim = parseData(s.data_fim);
            const atrasado = fim < hoje && s.status_servico !== 'concluido';
            const statusReal = atrasado ? 'atrasada' : (s.status_servico || 'pendente');
            const cor = (STATUS_COR[statusReal] || STATUS_COR.pendente).bg;
            const esq = pct(inicio, minDate, totalMs);
            const larg = pct(fim, minDate, totalMs) - esq;

            return (
              <div
                key={s.obraservicoid}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
              >
                {/* Nome */}
                <div
                  title={s.nome_servico}
                  style={{
                    width: 152,
                    flexShrink: 0,
                    fontSize: 13,
                    color: 'var(--text-primary)',
                    fontWeight: 500,
                    paddingRight: 8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.nome_servico}
                </div>

                {/* Área da barra */}
                <div style={{ flex: 1, position: 'relative', height: 28, background: 'var(--bg-page)', borderRadius: 4 }}>
                  {/* Linhas de grade dos meses */}
                  {marcadores.map((m) => (
                    <div
                      key={m.toISOString()}
                      style={{
                        position: 'absolute',
                        left: `${pct(m, minDate, totalMs)}%`,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        background: 'var(--border-color)',
                        pointerEvents: 'none',
                      }}
                    />
                  ))}

                  {/* Barra do serviço */}
                  <div
                    title={`${s.nome_servico}\n${inicio.toLocaleDateString('pt-BR')} → ${fim.toLocaleDateString('pt-BR')}`}
                    style={{
                      position: 'absolute',
                      left: `${esq}%`,
                      width: `${Math.max(larg, 0.5)}%`,
                      height: '100%',
                      background: cor,
                      borderRadius: 4,
                      opacity: 0.9,
                      cursor: 'default',
                    }}
                  />

                  {/* Linha do prazo */}
                  {prazoDate && prazoDate >= minDate && prazoDate <= maxDate && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${pct(prazoDate, minDate, totalMs)}%`,
                        top: -4,
                        bottom: -4,
                        width: 2,
                        zIndex: 2,
                        borderLeft: '2px dashed #dc2626',
                        pointerEvents: 'none',
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        top: -18,
                        left: 4,
                        fontSize: 10,
                        color: '#dc2626',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        background: 'var(--bg-card)',
                        padding: '0 2px',
                      }}>
                        {prazoDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Serviços sem data */}
        {(servicos || []).filter(s => !s.data_inicio || !s.data_fim).length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            * {(servicos || []).filter(s => !s.data_inicio || !s.data_fim).length} serviço(s) sem datas definidas não aparecem no cronograma.
          </div>
        )}
      </div>
    </div>
  );
}
